import modelPricingJson from "./rust/model-pricing/modelPricing.json";

export type ModelPricing = {
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion?: number;
  cacheWritePerMillion?: number;
};

const LONG_CONTEXT_SUFFIX = "@long";

export function baseModelId(model: string): string {
  return model.endsWith(LONG_CONTEXT_SUFFIX)
    ? model.slice(0, -LONG_CONTEXT_SUFFIX.length)
    : model;
}

// Providers meter usage under namespaced ids: Bedrock uses region-scoped
// inference profiles (`us.anthropic.claude-opus-4-8`) and OpenRouter uses a
// `<vendor>/<model>` slug (`anthropic/claude-sonnet-5`). Strip the prefix in
// either form so pricing keyed by the bare model id still resolves. Returns the
// id unchanged otherwise.
export function canonicalPricingModelId(model: string): string {
  const bare = model.includes("/")
    ? model.slice(model.lastIndexOf("/") + 1)
    : model;
  const match = /^(?:[a-z][a-z0-9-]*\.)?anthropic\.(.+)$/.exec(bare);
  return match ? match[1]! : bare;
}

type ModelPricingEntry = ModelPricing & {
  managed?: boolean;
  maxOutputTokens?: number;
  longContext?: ModelPricing & { thresholdTokens: number };
};

const modelPricingTable: Record<string, ModelPricingEntry> = modelPricingJson;

// List prices (USD per 1M tokens) for well-known models, keyed by the raw model
// id the provider returns (the dimension usage meters), used to seed connector
// pricing defaults; a user-entered rate always overrides. `<model>@long` rows
// price managed requests Pony records over the model’s long-context threshold.
// OpenAI models before GPT-5.6 bill cache writes at the uncached input rate.
// The orchestrator and Pony read the same JSON. Model ids:
// platform.claude.com/docs/en/about-claude/models/overview and
// developers.openai.com/api/docs/models/all
export const DEFAULT_MODEL_PRICING: Record<string, ModelPricing> = {};
// Managed usage has no aiProviderId, so pricing resolves through bare model ids.
export const MANAGED_MODEL_PRICING_ESTIMATES: Record<string, ModelPricing> = {};
for (const [
  model,
  { managed, maxOutputTokens: _, longContext, ...pricing },
] of Object.entries(modelPricingTable)) {
  const rows: Record<string, ModelPricing> = { [model]: pricing };
  if (longContext) {
    const { thresholdTokens: _, ...longPricing } = longContext;
    rows[`${model}${LONG_CONTEXT_SUFFIX}`] = longPricing;
  }
  Object.assign(DEFAULT_MODEL_PRICING, rows);
  if (managed) Object.assign(MANAGED_MODEL_PRICING_ESTIMATES, rows);
}
